// ─────────────────────────────────────────────
// KLOTHIQ — PDF Export Templates
// ─────────────────────────────────────────────

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COLOR, PAGE, FONT } from './pdfConstants';
import {
  formatCurrency,
  formatDate,
  formatStatus,
  drawHeader,
  drawSectionTitle,
  drawKeyValueRow,
  drawStatCards,
  getStatusColor,
  getAutoTableStyles,
  addPageNumbers,
} from './pdfHelpers';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProductRow {
  product_code?: string;
  name?: string;
  product_type?: string;
  type_name?: string;
  category_name?: string;
  variant_count?: number;
  default_price?: number;
  status?: string;
  created_at?: string;
  [key: string]: unknown;
}

export interface InventoryRow {
  sku?: string;
  product_name?: string;
  size?: string;
  color?: string | null;
  stock_qty?: number;
  cost_price?: number;
  stock_value?: number;
  [key: string]: unknown;
}

export interface SaleRow {
  sale_number?: string;
  sale_date?: string | Date;
  customer_name?: string | null;
  seller_name?: string | null;
  item_count?: number;
  subtotal?: number;
  discount?: number;
  tax?: number;
  total?: number;
  paid_amount?: number;
  return_review?: boolean;
  amount_paid?: number;
  paid?: number;
  balance_due?: number | null;
  pending_amount?: number;
  status?: string;
  sale_status?: string;
  payment_status?: string;
  payment_method?: string | null;
  [key: string]: unknown;
}

export interface PurchaseRow {
  po_number?: string;
  purchase_date?: string | Date;
  supplier_name?: string | null;
  item_count?: number;
  total?: number;
  amount_paid?: number;
  balance?: number | null;
  status?: string;
  [key: string]: unknown;
}

export interface SaleWithItems extends SaleRow {
  sale_items?: Array<{
    product_name?: string;
    sku?: string;
    size?: string;
    color?: string | null;
    quantity?: number;
    unit_price?: number;
    total_price?: number;
    [key: string]: unknown;
  }>;
  items?: Array<{
    name?: string;
    product_name?: string;
    sku?: string;
    size?: string;
    color?: string | null;
    quantity?: number;
    unit_price?: number;
    line_total?: number;
    total_price?: number;
    [key: string]: unknown;
  }>;
  customer?: {
    name?: string;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    pincode?: string | null;
    gst_number?: string | null;
  } | null;
  amount_paid?: number;
  paid_amount?: number;
  return_review?: boolean;
  cgst?: number;
  sgst?: number;
  taxable_amount?: number;
  subtotal_amount?: number;
  discount_amount?: number;
  total_amount?: number;
  balance_due?: number | null;
  notes?: string | null;
  [key: string]: unknown;
}

export interface PaymentRecord {
  reference_number?: string;
  receipt_number?: string;
  payment_date?: string | Date;
  payment_method?: string;
  transaction_id?: string | null;
  amount?: number;
  status?: string;
  customer_name?: string | null;
  supplier_name?: string | null;
  party_name?: string | null;
  party_phone?: string | null;
  sale_number?: string | null;
  po_number?: string | null;
  payment_type?: string;
  [key: string]: unknown;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getTodayISODate(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

// ── PDF TYPE 1 — PRODUCTS LIST ────────────────────────────────────────────────

/**
 * Generates and downloads a Products Catalog PDF report.
 */
export function exportProductsPdf(data: ProductRow[], _orgName?: string): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let startY = drawHeader(doc, 'Products Catalogue', `Exported: ${formatDate(new Date())}`);

  const safeData = data ?? [];
  const baseTableStyles = getAutoTableStyles();

  let activeCount = 0;
  let priceSum = 0;
  let lowStockCount = 0;

  const tableRows = safeData.map((row, idx) => {
    const code = row.product_code ?? (row.code as string) ?? '—';
    const name = row.name ?? '—';
    const type =
      row.product_type ?? row.type_name ?? (row.product_types as { name?: string })?.name ?? '—';
    const category = row.category_name ?? (row.categories as { name?: string })?.name ?? '—';
    const variants = row.variant_count ?? (row.product_variants as unknown[])?.length ?? 0;
    const price = Number(
      row.default_price ??
        row.price ??
        (row.product_variants as { default_price?: number }[])?.[0]?.default_price ??
        0,
    );
    const status =
      row.status ?? (row.is_active !== undefined ? (row.is_active ? 'Active' : 'Inactive') : '—');
    const statusLabel = formatStatus(status);

    if (/active/i.test(statusLabel)) activeCount += 1;
    if (Number(row.total_stock ?? row.stock_qty ?? -1) === 0) lowStockCount += 1;
    priceSum += price;

    return [
      (idx + 1).toString(),
      code,
      name,
      type,
      category,
      variants.toString(),
      formatCurrency(price),
      statusLabel,
    ];
  });

  startY = drawStatCards(
    doc,
    [
      { value: safeData.length.toString(), label: 'Total SKUs' },
      { value: activeCount.toString(), label: 'Active Listings' },
      {
        value: formatCurrency(safeData.length ? priceSum / safeData.length : 0),
        label: 'Avg. Price',
      },
      {
        value: lowStockCount.toString(),
        label: 'Needs Restock',
        trend: lowStockCount > 0 ? 'low stock' : undefined,
        trendUp: false,
      },
    ],
    startY,
  );

  autoTable(doc, {
    ...baseTableStyles,
    startY,
    head: [['#', 'Product Code', 'Name', 'Type', 'Category', 'Var.', 'Price', 'Status']],
    body: tableRows,
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 30, halign: 'left' },
      2: { cellWidth: 46, halign: 'left' },
      3: { cellWidth: 20, halign: 'left' },
      4: { cellWidth: 20, halign: 'left' },
      5: { cellWidth: 12, halign: 'center' },
      6: { cellWidth: 24, halign: 'right' },
      7: { cellWidth: 22, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 7) {
        data.cell.styles.textColor = getStatusColor(String(data.cell.raw));
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // Summary Row after table
  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? startY + 20;
  const summaryY = finalY + 6;

  if (summaryY < PAGE.height - PAGE.marginB - 10) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.textDark);
    doc.text(`Total products: ${safeData.length}`, PAGE.width - PAGE.marginR, summaryY, {
      align: 'right',
    });
  }

  addPageNumbers(doc);
  doc.save(`klothiq-products-${getTodayISODate()}.pdf`);
}

// ── PDF TYPE 2 — INVENTORY / STOCK ───────────────────────────────────────────

/**
 * Generates and downloads an Inventory Stock Report PDF.
 */
export function exportInventoryPdf(data: InventoryRow[], _orgName?: string): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let startY = drawHeader(doc, 'Inventory Management', `Stock as of ${formatDate(new Date())}`);

  const safeData = data ?? [];
  const baseTableStyles = getAutoTableStyles();

  let totalStockVal = 0;
  let outOfStockCount = 0;
  let lowStockCount = 0;

  const tableRows = safeData.map((row, idx) => {
    const sku = row.sku ?? '—';
    const product = row.product_name ?? '—';
    const size = row.size ?? '—';
    const color = row.color ?? '—';
    const qty = Number(
      row.stock_qty ?? (row.quantity_on_hand as number) ?? (row.quantity_available as number) ?? 0,
    );
    const cost = Number(
      row.cost_price ?? (row.weighted_avg_cost as number) ?? (row.default_price as number) ?? 0,
    );
    const val = Number(row.stock_value ?? qty * cost);
    totalStockVal += val;

    if (qty === 0) outOfStockCount += 1;
    else if (qty <= 10) lowStockCount += 1;

    return [
      (idx + 1).toString(),
      sku,
      product,
      size,
      color,
      qty.toString(),
      formatCurrency(cost),
      formatCurrency(val),
    ];
  });

  startY = drawStatCards(
    doc,
    [
      { value: safeData.length.toString(), label: 'Total SKUs' },
      { value: (safeData.length - outOfStockCount - lowStockCount).toString(), label: 'In Stock' },
      {
        value: lowStockCount.toString(),
        label: 'Low Stock',
        trend: lowStockCount > 0 ? 'needs reorder' : undefined,
        trendUp: lowStockCount === 0,
      },
      {
        value: outOfStockCount.toString(),
        label: 'Out of Stock',
        trend: outOfStockCount > 0 ? 'urgent' : undefined,
        trendUp: false,
      },
    ],
    startY,
  );

  autoTable(doc, {
    ...baseTableStyles,
    startY,
    head: [['#', 'SKU', 'Product', 'Size', 'Color', 'Stock Qty', 'Cost Price', 'Stock Value']],
    body: tableRows,
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 32, halign: 'left' },
      2: { cellWidth: 38, halign: 'left' },
      3: { cellWidth: 12, halign: 'center' },
      4: { cellWidth: 18, halign: 'left' },
      5: { cellWidth: 18, halign: 'center' },
      6: { cellWidth: 24, halign: 'right' },
      7: { cellWidth: 32, halign: 'right' },
    },
    didParseCell: (data) => {
      // Highlight stock quantity: red at 0, amber when low, green otherwise
      if (data.section === 'body' && data.column.index === 5) {
        const qty = Number(data.cell.raw);
        if (qty === 0) {
          data.cell.styles.textColor = COLOR.danger;
          data.cell.styles.fontStyle = 'bold';
        } else if (qty <= 10) {
          data.cell.styles.textColor = COLOR.warning;
          data.cell.styles.fontStyle = 'bold';
        } else {
          data.cell.styles.textColor = COLOR.success;
        }
      }
    },
  });

  // Summary Row after table
  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? startY + 20;
  const summaryY = finalY + 6;

  if (summaryY < PAGE.height - PAGE.marginB - 10) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.textDark);
    doc.text(`Total SKUs: ${safeData.length}`, PAGE.marginL, summaryY);
    doc.text(
      `Total Stock Value: ${formatCurrency(totalStockVal)}`,
      PAGE.width - PAGE.marginR,
      summaryY,
      { align: 'right' },
    );
  }

  addPageNumbers(doc);
  doc.save(`klothiq-inventory-${getTodayISODate()}.pdf`);
}

// ── PDF TYPE 3 — SALES LIST ───────────────────────────────────────────────────

/**
 * Generates and downloads a Sales List Report PDF.
 */
export function exportSalesPdf(data: SaleRow[], _orgName?: string): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let startY = drawHeader(doc, 'Sales Analytics', `Exported: ${formatDate(new Date())}`);

  const safeData = data ?? [];
  const baseTableStyles = getAutoTableStyles();

  let sumSubtotal = 0;
  let sumDiscount = 0;
  let sumTotal = 0;
  let sumPaid = 0;
  let sumBalanceDue = 0;
  let pendingCount = 0;
  const returnReviewCount = safeData.filter(
    (row) =>
      row.return_review ||
      /returned/i.test(String(row.status ?? row.sale_status)) ||
      row.return_status === 'completed',
  ).length;

  const tableRows = safeData.map((row, idx) => {
    const saleNum = row.sale_number ?? '—';
    const saleDate = formatDate(row.sale_date);
    const customer =
      row.customer_name ?? (row.customers as { name?: string })?.name ?? 'Walk-in Customer';
    const seller = row.seller_name ?? '—';
    const subtotal = Number(row.subtotal ?? (row.subtotal_amount as number) ?? row.total ?? 0);
    const discount = Number(row.discount ?? (row.discount_amount as number) ?? 0);
    const total = Number(row.total ?? (row.total_amount as number) ?? subtotal - discount);
    const paid = Number(
      row.paid_amount ??
        (row.amount_paid as number) ??
        (row.paid as number) ??
        (row.payment_status === 'paid' ? total : 0),
    );
    const orderStatus = row.status ?? (row.sale_status as string) ?? '—';
    const isReturned =
      /returned/i.test(String(orderStatus)) ||
      /returned/i.test(String(row.sale_status ?? '')) ||
      (row.return_status as string) === 'completed' ||
      Boolean(row.return_review);

    const balance =
      isReturned || row.balance_due === null
        ? 0
        : Number(row.balance_due ?? (row.pending_amount as number) ?? Math.max(0, total - paid));

    const rawPaymentStatus =
      (row.payment_status as string) ?? (balance <= 0 ? 'paid' : paid > 0 ? 'partial' : 'unpaid');
    const paymentLabel = formatStatus(rawPaymentStatus);

    sumSubtotal += subtotal;
    sumDiscount += discount;
    sumTotal += total;
    sumPaid += paid;
    sumBalanceDue += balance;

    if (
      !isReturned &&
      row.balance_due !== null &&
      (balance > 0 || /unpaid|partial|pending/i.test(rawPaymentStatus))
    ) {
      pendingCount += 1;
    }

    return [
      (idx + 1).toString(),
      saleNum,
      saleDate,
      customer,
      seller,
      formatCurrency(total),
      formatCurrency(paid),
      isReturned
        ? 'Review return'
        : row.balance_due === null
          ? 'Not applicable'
          : formatCurrency(balance),
      paymentLabel,
      formatStatus(orderStatus),
    ];
  });

  startY = drawStatCards(
    doc,
    [
      { value: safeData.length.toString(), label: 'Total Orders' },
      { value: formatCurrency(sumTotal), label: 'Original invoices' },
      { value: formatCurrency(sumPaid), label: 'Net Collected', accent: COLOR.teal },
      {
        value: formatCurrency(sumBalanceDue),
        label: 'Known Due',
        trend:
          returnReviewCount > 0
            ? `${returnReviewCount} return balances need review`
            : pendingCount > 0
              ? `${pendingCount} order${pendingCount > 1 ? 's' : ''} awaiting payment`
              : 'No known due',
        trendUp: sumBalanceDue === 0,
        accent: sumBalanceDue > 0 ? COLOR.danger : COLOR.purple,
      },
    ],
    startY,
  );

  autoTable(doc, {
    ...baseTableStyles,
    startY,
    styles: {
      ...(baseTableStyles.styles as Record<string, unknown>),
      cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
      fontSize: 7.5,
    },
    head: [
      [
        '#',
        'Sale #',
        'Date',
        'Customer',
        'Seller',
        'Total',
        'Paid',
        'Pending',
        'Payment',
        'Order Status',
      ],
    ],
    body: tableRows,
    columnStyles: {
      0: { cellWidth: 7, halign: 'center' },
      1: { cellWidth: 22, halign: 'left' },
      2: { cellWidth: 16, halign: 'left' },
      3: { cellWidth: 26, halign: 'left' },
      4: { cellWidth: 15, halign: 'left' },
      5: { cellWidth: 21, halign: 'right', fontStyle: 'bold' },
      6: { cellWidth: 21, halign: 'right' },
      7: { cellWidth: 21, halign: 'right' },
      8: { cellWidth: 16, halign: 'center' },
      9: { cellWidth: 17, halign: 'center' },
    },
    didParseCell: (data) => {
      // Pending amount column (col index 7): highlight in danger red if > 0
      if (data.section === 'body' && data.column.index === 7) {
        const rawText = String(data.cell.raw ?? '');
        const num = parseFloat(rawText.replace(/[^0-9.-]+/g, '')) || 0;
        if (num > 0) {
          data.cell.styles.textColor = COLOR.danger;
          data.cell.styles.fontStyle = 'bold';
        } else {
          data.cell.styles.textColor = COLOR.textMuted;
        }
      }
      // Payment Status column (col index 8): green for paid, red for unpaid, amber for partial
      if (data.section === 'body' && data.column.index === 8) {
        const status = String(data.cell.raw ?? '').toLowerCase();
        if (status.includes('paid') && !status.includes('unpaid')) {
          data.cell.styles.textColor = COLOR.success;
          data.cell.styles.fontStyle = 'bold';
        } else if (status.includes('unpaid')) {
          data.cell.styles.textColor = COLOR.danger;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [254, 242, 242]; // Soft red badge highlight
        } else if (status.includes('part')) {
          data.cell.styles.textColor = COLOR.warning;
          data.cell.styles.fontStyle = 'bold';
        }
      }
      // Order Status column (col index 9): highlight returned in purple, others per status
      if (data.section === 'body' && data.column.index === 9) {
        const status = String(data.cell.raw ?? '').toLowerCase();
        if (status.includes('return')) {
          data.cell.styles.textColor = COLOR.purple;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [245, 243, 255]; // Soft purple badge highlight
        } else {
          data.cell.styles.textColor = getStatusColor(String(data.cell.raw));
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  // Summary Rows after table (stacked right-aligned)
  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? startY + 20;
  let summaryY = finalY + 6;

  if (summaryY + 22 < PAGE.height - PAGE.marginB) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.textBody);

    doc.text(`Total orders:    ${safeData.length}`, PAGE.width - PAGE.marginR, summaryY, {
      align: 'right',
    });
    summaryY += 4;
    doc.text(
      `Original invoices: ${formatCurrency(sumTotal)}`,
      PAGE.width - PAGE.marginR,
      summaryY,
      {
        align: 'right',
      },
    );
    summaryY += 4;
    doc.setTextColor(...COLOR.success);
    doc.text(`Net collected:   ${formatCurrency(sumPaid)}`, PAGE.width - PAGE.marginR, summaryY, {
      align: 'right',
    });
    summaryY += 4.5;

    doc.setFont('helvetica', 'bold');
    if (sumBalanceDue > 0) {
      doc.setTextColor(...COLOR.danger);
      doc.text(
        `Total pending:   ${formatCurrency(sumBalanceDue)}`,
        PAGE.width - PAGE.marginR,
        summaryY,
        { align: 'right' },
      );
    } else {
      doc.setTextColor(...COLOR.teal);
      doc.text(
        `Total pending:   ${formatCurrency(0)} (All Paid)`,
        PAGE.width - PAGE.marginR,
        summaryY,
        { align: 'right' },
      );
    }
  }

  addPageNumbers(doc);
  doc.save(`klothiq-sales-${getTodayISODate()}.pdf`);
}

// ── PDF TYPE 4 — PURCHASES LIST ───────────────────────────────────────────────

/**
 * Generates and downloads a Purchases List Report PDF.
 */
export function exportPurchasesPdf(data: PurchaseRow[], _orgName?: string): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let startY = drawHeader(doc, 'Purchase Report', `Exported: ${formatDate(new Date())}`);

  const safeData = data ?? [];
  const baseTableStyles = getAutoTableStyles();

  let sumPurchased = 0;
  let sumPaid = 0;
  let sumBalances = 0;

  const tableRows = safeData.map((row, idx) => {
    const poNum = row.po_number ?? (row.purchase_number as string) ?? '—';
    const pDate = formatDate(row.purchase_date);
    const supplier = row.supplier_name ?? (row.suppliers as { name?: string })?.name ?? '—';
    const items =
      row.item_count ??
      (row.purchase_items as unknown[])?.length ??
      (row.details_mode === 'amount_only' ? 0 : null);
    const total = Number(row.total ?? (row.total_amount as number) ?? 0);
    const paid = Number(row.amount_paid ?? (row.paid_amount as number) ?? 0);
    const untracked =
      row.payment_status === 'not_recorded' ||
      (row.workflow_mode === 'record_only' && row.payment_tracking_mode === 'untracked') ||
      row.balance === null ||
      row.balance_due === null;
    const balance = untracked
      ? null
      : Number(row.balance ?? (row.balance_due as number) ?? total - paid);
    const status = row.status ?? '—';
    const statusLabel = formatStatus(
      row.workflow_mode === 'record_only'
        ? status === 'received'
          ? 'delivered'
          : status === 'ordered'
            ? 'pending'
            : status
        : status,
    );

    sumPurchased += total;
    sumPaid += paid;
    sumBalances += balance ?? 0;

    return [
      (idx + 1).toString(),
      poNum,
      pDate,
      supplier,
      items === null ? '—' : items.toString(),
      formatCurrency(total),
      formatCurrency(paid),
      balance === null ? 'Not tracked' : formatCurrency(balance),
      statusLabel,
    ];
  });

  startY = drawStatCards(
    doc,
    [
      { value: safeData.length.toString(), label: 'Total POs' },
      { value: formatCurrency(sumPurchased), label: 'Total Purchased' },
      { value: formatCurrency(sumPaid), label: 'Total Paid' },
      {
        value: formatCurrency(sumBalances),
        label: 'Tracked Outstanding',
        trend: sumBalances > 0 ? 'recorded debt' : 'untracked entries excluded',
        trendUp: sumBalances === 0,
      },
    ],
    startY,
  );

  autoTable(doc, {
    ...baseTableStyles,
    startY,
    head: [['#', 'PO #', 'Date', 'Supplier', 'Items', 'Total', 'Paid', 'Balance', 'Status']],
    body: tableRows,
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 20, halign: 'left' },
      2: { cellWidth: 20, halign: 'left' },
      3: { cellWidth: 24, halign: 'left' },
      4: { cellWidth: 14, halign: 'center' },
      5: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      6: { cellWidth: 24, halign: 'right' },
      7: { cellWidth: 24, halign: 'right' },
      8: { cellWidth: 20, halign: 'center' },
    },
    didParseCell: (data) => {
      // Highlight balance > 0 in danger red
      if (data.section === 'body' && data.column.index === 7) {
        const rawText = String(data.cell.raw || '');
        const val = parseFloat(rawText.replace(/[^\d.-]/g, ''));
        if (val > 0) {
          data.cell.styles.textColor = COLOR.danger;
          data.cell.styles.fontStyle = 'bold';
        }
      }
      if (data.section === 'body' && data.column.index === 8) {
        data.cell.styles.textColor = getStatusColor(String(data.cell.raw));
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // Summary Rows after table (stacked right-aligned)
  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? startY + 20;
  let summaryY = finalY + 6;

  if (summaryY + 16 < PAGE.height - PAGE.marginB) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.textBody);

    doc.text(`Total POs:        ${safeData.length}`, PAGE.width - PAGE.marginR, summaryY, {
      align: 'right',
    });
    summaryY += 4;
    doc.text(
      `Total purchased:  ${formatCurrency(sumPurchased)}`,
      PAGE.width - PAGE.marginR,
      summaryY,
      { align: 'right' },
    );
    summaryY += 4;
    doc.text(`Total paid:       ${formatCurrency(sumPaid)}`, PAGE.width - PAGE.marginR, summaryY, {
      align: 'right',
    });
    summaryY += 4.5;

    doc.setFont('helvetica', 'bold');
    if (sumBalances > 0) {
      doc.setTextColor(...COLOR.danger);
    } else {
      doc.setTextColor(...COLOR.teal);
    }
    doc.text(
      `Outstanding:      ${formatCurrency(sumBalances)}`,
      PAGE.width - PAGE.marginR,
      summaryY,
      { align: 'right' },
    );
  }

  addPageNumbers(doc);
  doc.save(`klothiq-purchases-${getTodayISODate()}.pdf`);
}

// ── PDF TYPE 5 — SALE RECEIPT / BILL ──────────────────────────────────────────

/**
 * Generates and downloads a Single Sale Detail Bill / Receipt PDF.
 */
export function buildSaleReceiptPdf(sale: SaleWithItems, _orgName?: string): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const saleNumber = sale.sale_number ?? 'RECEIPT';
  let currentY = drawHeader(doc, 'Sales Bill', saleNumber, true);

  // SECTION A — BILL DETAILS (blue-accented invoice info panel)
  const boxHeight = 22;
  doc.setFillColor(...COLOR.blueLight);
  doc.setDrawColor(...COLOR.blue);
  doc.setLineWidth(0.4);
  doc.rect(PAGE.marginL, currentY, PAGE.contentW, boxHeight, 'FD');

  const leftColX = PAGE.marginL + 4;
  const rightColX = PAGE.width / 2 + 4;
  let billY = currentY + 6;
  const statusLabel = formatStatus(sale.status ?? (sale.sale_status as string));

  drawKeyValueRow(doc, 'Bill No', saleNumber, leftColX, billY);
  drawKeyValueRow(doc, 'Payment', sale.payment_method ?? '—', rightColX, billY);

  billY += 5.5;
  drawKeyValueRow(doc, 'Date', formatDate(sale.sale_date), leftColX, billY);
  drawKeyValueRow(
    doc,
    'Status',
    statusLabel,
    rightColX,
    billY,
    undefined,
    getStatusColor(statusLabel),
  );

  billY += 5.5;
  if (sale.seller_name) drawKeyValueRow(doc, 'Seller', sale.seller_name, leftColX, billY);

  currentY += boxHeight + 6;

  // SECTION B — CUSTOMER DETAILS
  currentY = drawSectionTitle(doc, 'Bill To', currentY);

  const custName = sale.customer_name ?? sale.customer?.name ?? 'Walk-in Customer';
  const custPhone = sale.customer?.phone;
  const custAddress = [sale.customer?.address, sale.customer?.city, sale.customer?.pincode]
    .filter(Boolean)
    .join(', ');
  autoTable(doc, {
    startY: currentY,
    theme: 'plain',
    margin: { left: PAGE.marginL, right: PAGE.marginR },
    body: [
      ['Customer', custName],
      ...(custPhone ? [['Phone', custPhone]] : []),
      ...(custAddress ? [['Address', custAddress]] : []),
    ],
    styles: {
      font: 'helvetica',
      fontSize: FONT.small,
      cellPadding: 3,
      fillColor: COLOR.rowAlt,
      textColor: COLOR.textBody,
      overflow: 'linebreak',
    },
    columnStyles: {
      0: { cellWidth: 25, textColor: COLOR.textMuted },
      1: { cellWidth: PAGE.contentW - 25, fontStyle: 'bold' },
    },
  });
  currentY =
    ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ??
      currentY + 22) + 6;
  if (currentY > PAGE.height - PAGE.marginB - 35) {
    doc.addPage();
    currentY = PAGE.marginT + 10;
  }

  // SECTION C — ITEMS TABLE
  currentY = drawSectionTitle(doc, 'Items', currentY);

  const rawItems = sale.sale_items ?? sale.items ?? [];
  const itemRows = rawItems.map((item, idx) => {
    const pName = item.product_name ?? item.name ?? 'Product';
    const size = item.size ?? '—';
    const color = item.color ?? '—';
    const qty = Number(item.quantity ?? 1);
    const rate = Number(item.unit_price ?? 0);
    const amt = Number(item.total_price ?? item.line_total ?? qty * rate);

    return [
      (idx + 1).toString(),
      pName,
      size,
      color,
      qty.toString(),
      formatCurrency(rate),
      formatCurrency(amt),
    ];
  });

  const baseTableStyles = getAutoTableStyles();
  autoTable(doc, {
    ...baseTableStyles,
    startY: currentY,
    head: [['#', 'Product', 'Size', 'Color', 'Qty', 'Rate', 'Amount']],
    body: itemRows,
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 76, halign: 'left' },
      2: { cellWidth: 14, halign: 'center' },
      3: { cellWidth: 18, halign: 'left' },
      4: { cellWidth: 14, halign: 'center' },
      5: { cellWidth: 26, halign: 'right' },
      6: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
    },
  });

  currentY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ??
    currentY + 30;
  currentY += 5;

  // SECTION D & E — TOTALS & PAYMENT SUMMARY
  const isReturned =
    /returned/i.test(String(sale.status ?? '')) ||
    /returned/i.test(String(sale.sale_status ?? '')) ||
    (sale as Record<string, unknown>).return_status === 'completed';

  const subtotal = Number(sale.subtotal ?? sale.subtotal_amount ?? sale.total ?? 0);
  const discount = Number(sale.discount ?? sale.discount_amount ?? 0);
  const deliveryCharge = Number((sale as Record<string, unknown>).delivery_charge ?? 0);
  const grandTotal = Number(
    sale.total ?? sale.total_amount ?? subtotal - discount + deliveryCharge,
  );
  const paid = Number(sale.amount_paid ?? sale.paid_amount ?? (isReturned ? 0 : grandTotal));
  const financial = sale.financial_summary as
    | { amount_due: number | null; return_review: boolean; collected?: number; refunded?: number }
    | undefined;
  const balanceDue = financial
    ? financial.amount_due
    : isReturned
      ? null
      : Math.max(0, grandTotal - paid);
  const reviewReturn = isReturned || financial?.return_review;

  // Check if page has enough room for Totals (requires ~45mm)
  if (currentY + 45 > PAGE.height - PAGE.marginB - 25) {
    doc.addPage();
    currentY = PAGE.marginT + 10;
  }

  // Left Column of Summary: Payment Status
  const paySummaryX = PAGE.marginL;
  let paySummaryY = currentY + 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.textMuted);
  if (financial?.refunded) {
    doc.text(
      `Received: ${formatCurrency(financial.collected ?? paid + financial.refunded)}`,
      paySummaryX,
      paySummaryY,
    );
    paySummaryY += 5;
    doc.text(`Refunded: ${formatCurrency(financial.refunded)}`, paySummaryX, paySummaryY);
    paySummaryY += 5;
  }
  doc.text(financial?.refunded ? 'Net Paid:' : 'Amount Paid:', paySummaryX, paySummaryY);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.textBody);
  doc.text(formatCurrency(paid), paySummaryX + 26, paySummaryY);

  paySummaryY += 6;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLOR.textMuted);
  doc.text('Balance Due:', paySummaryX, paySummaryY);
  doc.setFont('helvetica', 'bold');
  if (!reviewReturn && balanceDue !== null && balanceDue > 0) {
    doc.setTextColor(...COLOR.danger);
    doc.text(formatCurrency(balanceDue), paySummaryX + 26, paySummaryY);
  } else if (reviewReturn) {
    doc.setTextColor(...COLOR.textMuted);
    doc.text('Review return adjustment', paySummaryX + 26, paySummaryY);
  } else if (balanceDue === null) {
    doc.text('Not applicable', paySummaryX + 26, paySummaryY);
  } else {
    doc.setTextColor(...COLOR.success);
    doc.text(`${formatCurrency(0)} (Paid)`, paySummaryX + 26, paySummaryY);
  }

  // Right Column of Summary: Vertical stack of totals
  const totalsBlockW = 65;
  const totalsBlockX = PAGE.width - PAGE.marginR - totalsBlockW;
  let totalStackY = currentY + 4;

  const drawTotalLine = (label: string, value: string, isDanger = false) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.textMuted);
    doc.text(label, totalsBlockX, totalStackY);

    doc.setFont('helvetica', 'bold');
    if (isDanger) {
      doc.setTextColor(...COLOR.danger);
    } else {
      doc.setTextColor(...COLOR.textBody);
    }
    doc.text(value, totalsBlockX + totalsBlockW, totalStackY, { align: 'right' });
    totalStackY += 4.5;
  };

  drawTotalLine('Subtotal:', formatCurrency(subtotal));
  if (discount > 0) {
    drawTotalLine('Discount:', `-${formatCurrency(discount)}`, true);
  }
  if (deliveryCharge > 0) {
    drawTotalLine('Delivery:', formatCurrency(deliveryCharge));
  }

  // Divider
  doc.setDrawColor(...COLOR.border);
  doc.setLineWidth(0.2);
  doc.line(totalsBlockX, totalStackY - 1, totalsBlockX + totalsBlockW, totalStackY - 1);
  totalStackY += 2;

  // GRAND TOTAL — ink bar with gold amount
  totalStackY += 1;
  const grandTotalBoxH = 8;
  doc.setFillColor(...COLOR.ink);
  doc.rect(totalsBlockX, totalStackY - 1, totalsBlockW, grandTotalBoxH, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...COLOR.white);
  doc.text('GRAND TOTAL', totalsBlockX + 3, totalStackY + 4.5);
  doc.setTextColor(...COLOR.gold);
  doc.text(formatCurrency(grandTotal), totalsBlockX + totalsBlockW - 3, totalStackY + 4.5, {
    align: 'right',
  });

  // FOOTER NOTE (centered at bottom)
  const noteY = PAGE.height - PAGE.marginB - 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.textMuted);
  doc.text('Thank you for shopping with Klothiq!', PAGE.width / 2, noteY, { align: 'center' });
  doc.text('+91 81390 55870 · Darmasala, Kannur, Kerala', PAGE.width / 2, noteY + 3.5, {
    align: 'center',
  });
  doc.text(
    'This is a computer-generated receipt and does not require a signature.',
    PAGE.width / 2,
    noteY + 7,
    { align: 'center' },
  );

  addPageNumbers(doc, true);
  return doc;
}

/** Keep existing download/print callers on the same receipt document. */
export function exportSaleReceiptPdf(sale: SaleWithItems, orgName?: string): void {
  const doc = buildSaleReceiptPdf(sale, orgName);
  doc.save(`klothiq-receipt-${sale.sale_number ?? 'RECEIPT'}-${getTodayISODate()}.pdf`);
}

// ── PDF TYPE 6 — PAYMENT RECEIPT ──────────────────────────────────────────────

/**
 * Generates and downloads a Payment Receipt / Voucher PDF.
 */
export function exportPaymentReceiptPdf(payment: PaymentRecord, _orgName?: string): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const refNum = payment.reference_number ?? payment.receipt_number ?? 'PAYMENT';
  let currentY = drawHeader(doc, 'Payment Receipt', `Ref: ${refNum}`);

  const boxHeight = 22;
  const leftColX = PAGE.marginL + 4;
  const rightColX = PAGE.width / 2 + 4;

  // SECTION A — PAYMENT DETAILS
  currentY = drawSectionTitle(doc, 'Payment Details', currentY);

  doc.setFillColor(...COLOR.rowAlt);
  doc.setDrawColor(...COLOR.border);
  doc.setLineWidth(0.3);
  doc.rect(PAGE.marginL, currentY, PAGE.contentW, boxHeight, 'FD');

  let payY = currentY + 6;
  drawKeyValueRow(doc, 'Receipt No', refNum, leftColX, payY);
  drawKeyValueRow(doc, 'Method', formatStatus(payment.payment_method), rightColX, payY);

  payY += 5.5;
  drawKeyValueRow(doc, 'Date', formatDate(payment.payment_date), leftColX, payY);
  drawKeyValueRow(doc, 'Transaction ID', payment.transaction_id ?? '—', rightColX, payY);

  currentY += boxHeight + 6;

  // SECTION B — PARTY INFO
  currentY = drawSectionTitle(doc, 'Party Information', currentY);

  doc.setFillColor(...COLOR.rowAlt);
  doc.setDrawColor(...COLOR.border);
  doc.setLineWidth(0.3);
  doc.rect(PAGE.marginL, currentY, PAGE.contentW, boxHeight, 'FD');

  let partyY = currentY + 6;
  const partyName = payment.customer_name ?? payment.supplier_name ?? payment.party_name ?? '—';
  const linkedRef = payment.sale_number ?? payment.po_number ?? '—';
  const pType = payment.payment_type ? formatStatus(payment.payment_type) : 'Sale / Purchase';

  drawKeyValueRow(doc, 'Paid by / To', partyName, leftColX, partyY);
  drawKeyValueRow(doc, 'Linked To', linkedRef, rightColX, partyY);

  partyY += 5.5;
  drawKeyValueRow(doc, 'Phone', payment.party_phone ?? '—', leftColX, partyY);
  drawKeyValueRow(doc, 'Type', pType, rightColX, partyY);

  currentY += boxHeight + 10;

  // SECTION C — AMOUNT BLOCK (large, centered)
  const amountBoxW = 100;
  const amountBoxH = 36;
  const amountBoxX = (PAGE.width - amountBoxW) / 2;

  doc.setFillColor(...COLOR.ink);
  doc.setDrawColor(...COLOR.gold);
  doc.setLineWidth(0.6);
  doc.roundedRect(amountBoxX, currentY, amountBoxW, amountBoxH, 2, 2, 'FD');

  let centerTextY = currentY + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.goldLight);
  doc.text('AMOUNT RECEIVED', PAGE.width / 2, centerTextY, { align: 'center' });

  centerTextY += 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...COLOR.gold);
  doc.text(formatCurrency(Number(payment.amount ?? 0)), PAGE.width / 2, centerTextY, {
    align: 'center',
  });

  // Status Badge Pill below amount
  centerTextY += 9;
  const isPaid = payment.status?.toLowerCase() === 'paid' || !payment.status;
  const badgeText = isPaid ? 'PAID' : 'PENDING';
  const badgeColor = isPaid ? COLOR.success : COLOR.warning;
  const badgeW = 28;
  const badgeH = 5.5;
  const badgeX = (PAGE.width - badgeW) / 2;

  doc.setFillColor(...badgeColor);
  doc.roundedRect(badgeX, centerTextY - 3.8, badgeW, badgeH, 2.5, 2.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.white);
  doc.text(badgeText, PAGE.width / 2, centerTextY, { align: 'center' });

  // SECTION D — FOOTER NOTE
  const noteY = PAGE.height - PAGE.marginB - 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.textMuted);
  doc.text('This payment has been recorded in Klothiq Admin.', PAGE.width / 2, noteY, {
    align: 'center',
  });
  doc.text('+91 81390 55870 · Darmasala, Kannur, Kerala', PAGE.width / 2, noteY + 3.5, {
    align: 'center',
  });
  doc.text(
    'This is a computer-generated receipt and does not require a signature.',
    PAGE.width / 2,
    noteY + 7,
    { align: 'center' },
  );

  addPageNumbers(doc);
  doc.save(`klothiq-payment-${refNum}-${getTodayISODate()}.pdf`);
}
